/**
 * Error Logging Utilities
 * 
 * This file contains utilities for comprehensive error logging and formatting.
 * It provides functions for detailed error logging and user-friendly error messages.
 */

/**
 * Formats an error object into a detailed error message
 * @param {Error} error - The error object
 * @param {Object} context - Additional context information
 * @returns {string} Formatted error message
 */
export const formatErrorDetails = (error, context = {}) => {
  try {
    // Start with basic error info
    const details = [
      `Error: ${error.message || 'Unknown error'}`,
      `Type: ${error.name || 'Error'}`,
      `Timestamp: ${new Date().toISOString()}`
    ];

    // Add stack trace if available
    if (error.stack) {
      details.push(`Stack Trace: ${error.stack}`);
    }

    // Add HTTP-specific details if available
    if (error.response) {
      details.push(`Status: ${error.response.status} ${error.response.statusText}`);

      // Add response data if available
      if (error.response.data) {
        const responseData = typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data, null, 2)
          : error.response.data;
        details.push(`Response Data: ${responseData}`);
      }

      // Add response headers if available
      if (error.response.headers) {
        details.push(`Response Headers: ${JSON.stringify(error.response.headers, null, 2)}`);
      }
    }

    // Add request details if available
    if (error.config) {
      details.push(`Request URL: ${error.config.method?.toUpperCase() || 'GET'} ${error.config.url}`);

      // Add request headers if available
      if (error.config.headers) {
        details.push(`Request Headers: ${JSON.stringify(error.config.headers, null, 2)}`);
      }

      // Add request data if available
      if (error.config.data) {
        const requestData = typeof error.config.data === 'object'
          ? JSON.stringify(error.config.data, null, 2)
          : error.config.data;
        details.push(`Request Data: ${requestData}`);
      }
    }

    // Add additional context information
    if (Object.keys(context).length > 0) {
      details.push('Context:');
      Object.entries(context).forEach(([key, value]) => {
        const formattedValue = typeof value === 'object'
          ? JSON.stringify(value, null, 2)
          : value;
        details.push(`  ${key}: ${formattedValue}`);
      });
    }

    return details.join('\n');
  } catch (formatError) {
    // Fallback if there's an error in the formatting logic
    console.error('Error formatting error details:', formatError);
    return `Error: ${error.message || 'Unknown error'} (Error details formatting failed)`;
  }
};

/**
 * Logs an error with detailed information
 * @param {Error} error - The error object
 * @param {string} operation - The operation that was being performed
 * @param {Object} params - The parameters that were used
 * @param {Object} additionalContext - Any additional context
 * @returns {string} - The formatted error details
 */
export const logError = (error, operation, params = {}, additionalContext = {}) => {
  const context = {
    operation,
    params,
    ...additionalContext
  };

  const errorDetails = formatErrorDetails(error, context);

  // Log to console
  console.error('DETAILED ERROR LOG:');
  console.error(errorDetails);

  // Here you could also send the error to a logging service
  // or store it in a database for later analysis

  return errorDetails;
};

/**
 * Creates a user-friendly error message from a detailed error
 * @param {Error} error - The error object
 * @param {string} operation - The operation that was being performed
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyErrorMessage = (error, operation) => {
  // Start with a generic message
  let message = `An error occurred while ${operation}.`;

  // Add more specific information based on error type
  if (error.response) {
    // Server responded with an error status
    if (error.response.status === 400) {
      message = `Invalid request while ${operation}. Please check your inputs.`;
    } else if (error.response.status === 401) {
      message = `Authentication required. Please log in again.`;
    } else if (error.response.status === 403) {
      message = `You don't have permission to ${operation}.`;
    } else if (error.response.status === 404) {
      message = `The resource needed to ${operation} was not found.`;
    } else if (error.response.status === 500) {
      message = `Server error while ${operation}. Please try again later.`;
    }

    // Add server message if available
    if (error.response.data && error.response.data.message) {
      message += ` Server message: ${error.response.data.message}`;
    }
  } else if (error.request) {
    // Request was made but no response received
    message = `No response received while ${operation}. Please check your network connection.`;
  } else {
    // Something else went wrong
    message = `Error while ${operation}: ${error.message}`;
  }

  return message;
};
